export const SITE_URL = "https://dogufy.com";
export const DEFAULT_LOCALE = "en";

export const locales = [
  "en",
  "id",
  "vi",
  "cs",
  "es",
  "fr",
  "de",
  "it",
  "hu",
  "ms",
  "nl",
  "pl",
  "pt",
  "th",
  "tr",
  "el",
  "uk",
  "ru",
  "ar",
  "ko",
  "ja",
  "ro",
] as const;

export type Locale = (typeof locales)[number];

export const localeLabels: Record<Locale, string> = {
  en: "English",
  id: "Indonesia",
  vi: "Tiếng Việt",
  cs: "Čeština",
  es: "Español",
  fr: "Français",
  de: "Deutsch",
  it: "Italiano",
  hu: "Magyar",
  ms: "Bahasa Melayu",
  nl: "Nederlands",
  pl: "Polski",
  pt: "Português",
  th: "ไทย",
  tr: "Türkçe",
  el: "Ελληνικά",
  uk: "Українська",
  ru: "Русский",
  ar: "العربية",
  ko: "한국어",
  ja: "日本語",
  ro: "Română",
};

export const localeOgMap: Partial<Record<Locale, string>> = {
  en: "en_US",
  id: "id_ID",
  vi: "vi_VN",
  cs: "cs_CZ",
  es: "es_ES",
  fr: "fr_FR",
  de: "de_DE",
  it: "it_IT",
  hu: "hu_HU",
  ms: "ms_MY",
  nl: "nl_NL",
  pl: "pl_PL",
  pt: "pt_PT",
  th: "th_TH",
  tr: "tr_TR",
  el: "el_GR",
  uk: "uk_UA",
  ru: "ru_RU",
  ar: "ar_AR",
  ko: "ko_KR",
  ja: "ja_JP",
  ro: "ro_RO",
};

export function isLocale(value: string): value is Locale {
  return locales.includes(value as Locale);
}

export function getLocaleFromPathname(pathname: string): Locale {
  const [firstSegment] = pathname.split("/").filter(Boolean);
  return firstSegment && isLocale(firstSegment) ? firstSegment : DEFAULT_LOCALE;
}

export function stripLocaleFromPathname(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] && isLocale(segments[0])) {
    const rest = segments.slice(1).join("/");
    return rest ? `/${rest}` : "/";
  }
  return pathname || "/";
}

export function withLocale(locale: Locale, path = "/"): string {
  if (!path || path === "/") {
    return `/${locale}`;
  }

  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const strippedPath = stripLocaleFromPathname(normalizedPath);
  return strippedPath === "/" ? `/${locale}` : `/${locale}${strippedPath}`;
}

export function buildLocaleAlternates(path = "/") {
  return Object.fromEntries(
    locales.map((locale) => [locale, `${SITE_URL}${withLocale(locale, path)}`])
  );
}

export function getLocalizedSiteTitle(locale: Locale) {
  const titles: Record<Locale, string> = {
    en: "Dogufy - Free Online PDF, Image & Text Tools",
    id: "Dogufy - Tools PDF, Gambar, dan Teks Gratis Online",
    vi: "Dogufy - Công Cụ PDF, Hình Ảnh và Văn Bản Miễn Phí Trực Tuyến",
    cs: "Dogufy - Bezplatné Online Nástroje Pro PDF, Obrázky a Text",
    es: "Dogufy - Herramientas Online Gratis para PDF, Imágenes y Texto",
    fr: "Dogufy - Outils En Ligne Gratuits pour PDF, Images et Texte",
    de: "Dogufy - Kostenlose Online-Tools für PDF, Bilder und Text",
    it: "Dogufy - Strumenti Online Gratuiti per PDF, Immagini e Testo",
    hu: "Dogufy - Ingyenes Online PDF-, Kép- és Szövegeszközök",
    ms: "Dogufy - Alat PDF, Imej dan Teks Percuma Dalam Talian",
    nl: "Dogufy - Gratis Online Tools voor PDF, Afbeeldingen en Tekst",
    pl: "Dogufy - Darmowe Narzędzia Online do PDF, Obrazów i Tekstu",
    pt: "Dogufy - Ferramentas Online Gratuitas para PDF, Imagens e Texto",
    th: "Dogufy - เครื่องมือ PDF รูปภาพ และข้อความฟรีออนไลน์",
    tr: "Dogufy - Ücretsiz Çevrimiçi PDF, Görsel ve Metin Araçları",
    el: "Dogufy - Δωρεάν Online Εργαλεία για PDF, Εικόνες και Κείμενο",
    uk: "Dogufy - Безкоштовні Онлайн-Інструменти для PDF, Зображень і Тексту",
    ru: "Dogufy - Бесплатные Онлайн-Инструменты для PDF, Изображений и Текста",
    ar: "Dogufy - أدوات PDF والصور والنصوص المجانية عبر الإنترنت",
    ko: "Dogufy - 무료 온라인 PDF, 이미지, 텍스트 도구",
    ja: "Dogufy - 無料オンライン PDF・画像・テキストツール",
    ro: "Dogufy - Unelte Online Gratuite pentru PDF, Imagini și Text",
  };

  return titles[locale] ?? titles.en;
}

export function getLocalizedSiteDescription(locale: Locale) {
  const descriptions: Record<Locale, string> = {
    en: "Dogufy offers free online PDF, image, and text tools with fast processing, strong technical SEO, and AI-friendly structured content.",
    id: "Dogufy menyediakan tools PDF, gambar, dan teks gratis online dengan proses cepat, SEO teknis yang kuat, dan struktur konten yang ramah AI.",
    vi: "Dogufy cung cấp công cụ PDF, hình ảnh và văn bản miễn phí trực tuyến với xử lý nhanh, SEO kỹ thuật tốt và cấu trúc nội dung thân thiện với AI.",
    cs: "Dogufy nabízí bezplatné online nástroje pro PDF, obrázky a text s rychlým zpracováním, silným technickým SEO a strukturou obsahu přívětivou pro AI.",
    es: "Dogufy ofrece herramientas online gratis para PDF, imágenes y texto con procesamiento rápido, SEO técnico sólido y contenido estructurado apto para IA.",
    fr: "Dogufy propose des outils gratuits en ligne pour PDF, images et texte avec un traitement rapide, un SEO technique solide et une structure de contenu adaptée à l’IA.",
    de: "Dogufy bietet kostenlose Online-Tools für PDF, Bilder und Text mit schneller Verarbeitung, starkem technischem SEO und KI-freundlicher Inhaltsstruktur.",
    it: "Dogufy offre strumenti online gratuiti per PDF, immagini e testo con elaborazione rapida, SEO tecnico solido e contenuti strutturati adatti all’AI.",
    hu: "A Dogufy ingyenes online PDF-, kép- és szövegeszközöket kínál gyors feldolgozással, erős technikai SEO-val és MI-barát tartalmi struktúrával.",
    ms: "Dogufy menawarkan alat PDF, imej dan teks percuma dalam talian dengan pemprosesan pantas, SEO teknikal yang kukuh dan struktur kandungan mesra AI.",
    nl: "Dogufy biedt gratis online tools voor PDF, afbeeldingen en tekst met snelle verwerking, sterke technische SEO en een AI-vriendelijke contentstructuur.",
    pl: "Dogufy oferuje darmowe narzędzia online do PDF, obrazów i tekstu z szybkim przetwarzaniem, mocnym technicznym SEO i strukturą treści przyjazną dla AI.",
    pt: "A Dogufy oferece ferramentas online gratuitas para PDF, imagens e texto com processamento rápido, SEO técnico forte e estrutura de conteúdo amiga da IA.",
    th: "Dogufy ให้บริการเครื่องมือ PDF รูปภาพ และข้อความฟรีออนไลน์ พร้อมการประมวลผลที่รวดเร็ว SEO เชิงเทคนิคที่แข็งแรง และโครงสร้างคอนเทนต์ที่เป็นมิตรกับ AI.",
    tr: "Dogufy; hızlı işleme, güçlü teknik SEO ve yapay zekâ dostu yapılandırılmış içerikle ücretsiz çevrimiçi PDF, görsel ve metin araçları sunar.",
    el: "Το Dogufy προσφέρει δωρεάν online εργαλεία για PDF, εικόνες και κείμενο με γρήγορη επεξεργασία, ισχυρό τεχνικό SEO και δομημένο περιεχόμενο φιλικό προς AI.",
    uk: "Dogufy пропонує безкоштовні онлайн-інструменти для PDF, зображень і тексту з швидкою обробкою, сильним технічним SEO та структурованим контентом, дружнім до AI.",
    ru: "Dogufy предлагает бесплатные онлайн-инструменты для PDF, изображений и текста с быстрой обработкой, сильным техническим SEO и структурированным контентом, удобным для AI.",
    ar: "يوفر Dogufy أدوات مجانية عبر الإنترنت لملفات PDF والصور والنصوص مع معالجة سريعة وSEO تقني قوي وبنية محتوى مناسبة للذكاء الاصطناعي.",
    ko: "Dogufy는 빠른 처리, 강력한 기술 SEO, AI 친화적인 구조화 콘텐츠와 함께 무료 온라인 PDF, 이미지, 텍스트 도구를 제공합니다.",
    ja: "Dogufy は、高速処理・強い技術SEO・AIフレンドリーな構造化コンテンツを備えた無料オンライン PDF・画像・テキストツールを提供します。",
    ro: "Dogufy oferă unelte online gratuite pentru PDF, imagini și text, cu procesare rapidă, SEO tehnic puternic și structură de conținut prietenoasă cu AI.",
  };

  return descriptions[locale] ?? descriptions.en;
}
